import { useAccountContext } from "~/v2/providers/AccountProvider";
import { AssetSearch } from "~/v3-poc/features/asset-search/AssetSearch.connector";
import { useGroupSymbolMutation } from "../../shared/groups/useGroupSymbolMutation";
import {
  useWorkspace,
  useWorkspaceStore,
} from "../../shared/workspace/Workspace.context";
import type { GroupId } from "../../shared/workspace/workspace.types";
import { getEffectiveWidgetSymbol } from "../../shared/workspace/workspace.utils";
import { useOptionsChainWidgetController } from "./OptionsChainWidget.controller";
import { OptionsChainWidgetPresentation } from "./OptionsChainWidget.pres";
import { useOptionsChainPreferences } from "./useOptionsChainPreferences";

export interface OptionsChainWidgetConnectorProps {
  id: string;
}

/**
 * Options-chain widget. Symbol search stays in the toolbar whether the chain
 * is grouped or not. Ungrouped, a pick names this widget only. Grouped, the
 * field shows the group's symbol and a pick republishes it so linked widgets
 * stay in step. A chain on its own is still read-only for quotes: a leg has
 * nowhere to go without a group.
 *
 * Bid/ask clicks route one leg atomically through the workspace
 * store, which resolves the target ticket (same group → unassigned → create).
 *
 * Everything is polled on a three-second interval. Data still arrives on three
 * cadences and the widget never pretends otherwise: quotes are current, trades
 * and volume are 15 minutes behind unless the account reaches the OPRA feed, and
 * open interest is a daily batch two sessions back.
 */
export const OptionsChainWidget = ({
  id,
}: OptionsChainWidgetConnectorProps) => {
  const { currentAccount } = useAccountContext();
  const store = useWorkspaceStore();

  const {
    preferences,
    setStrikeCount,
    setSideFilter,
    setExpirationFilter,
    setExpirationRange,
    setExpirationCount,
    setMetricVisibility,
    selectAllMetrics,
    resetMetrics,
    reorderMetrics,
  } = useOptionsChainPreferences();

  // Primitive selectors — safe under Zustand v5 referential-equality bailout.
  const groupId = useWorkspace(
    (s): GroupId | null => s.widgets[id]?.groupId ?? null,
  );
  const { setGroupSymbol, groupSymbolError } = useGroupSymbolMutation(groupId);
  const groupSymbol = useWorkspace((s) => getEffectiveWidgetSymbol(s, id));

  // Immer keeps both records referentially stable until they are mutated, so
  // the highlight list is derived in useMemo rather than inside the selector.
  const widgets = useWorkspace((s) => s.widgets);
  const tickets = useWorkspace((s) => s.tickets);

  /**
   * Prefer `options_trading_level` (already capped by user/admin/correspondent);
   * live accounts often only report `options_approved_level`. Absent means
   * unknown — allow trading and let the order ticket reject.
   */
  const optionsTradingLevel =
    currentAccount?.options_trading_level ??
    currentAccount?.options_approved_level ??
    null;

  const {
    searchValue,
    onSearchQueryChange,
    onSymbolSelect,
    onSymbolClear,
    ...presentation
  } = useOptionsChainWidgetController({
    groupId,
    groupSymbol,
    widgets,
    tickets,
    store,
    setGroupSymbol,
    optionsTradingLevel,
    preferences,
    setStrikeCount,
    setSideFilter,
    setExpirationFilter,
    setExpirationRange,
    setExpirationCount,
    setMetricVisibility,
    selectAllMetrics,
    resetMetrics,
    reorderMetrics,
  });

  const symbolSearch = (
    <AssetSearch
      placeholder="Search symbol..."
      showFilters={false}
      value={searchValue}
      onChange={onSearchQueryChange}
      onSelect={onSymbolSelect}
      onClear={onSymbolClear}
    />
  );

  return (
    <OptionsChainWidgetPresentation
      {...presentation}
      symbolSearch={symbolSearch}
      groupSymbolError={groupSymbolError}
    />
  );
};
